import React, { useState } from 'react';
import { useNavigation, CrudFilter } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Grid,
  Select,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  FilterOutlined,
  ClearOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Player, PlayerCohort } from '@/types';
import { ExportButton } from '@/components/ExportButton';
import { exportOptions } from '@/constants/exportOptions';
import { adminApi } from '@/services/api';
import { PlayerSummaryPanels } from './PlayerSummaryPanels';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type StatusFilter = 'all' | 'active' | 'inactive' | 'banned' | 'deleted';

/**
 * Retention/engagement cohort filter (§3.4). The Player activity and Players
 * with no matches panels above the list set it. Maps directly to the backend's
 * ?cohort=... query param.
 */
type CohortFilter = 'all' | PlayerCohort;

const COHORT_LABELS: Record<PlayerCohort, string> = {
  'no-matches': 'No matches',
  'quickplay-only': 'Quickplay only',
  'at-risk': 'At-risk (7-30d)',
  churned: 'Churned (>30d)',
};

// §3.4a — 21 division tiers, low→high. `divisionTier` query param on the
// backend filters via EXISTS against user_divisions.
const DIVISION_OPTIONS: Array<{ tier: number; label: string }> = [
  { tier: 1, label: '1 — Rookie III' },
  { tier: 2, label: '2 — Rookie II' },
  { tier: 3, label: '3 — Rookie I' },
  { tier: 4, label: '4 — Amateur III' },
  { tier: 5, label: '5 — Amateur II' },
  { tier: 6, label: '6 — Amateur I' },
  { tier: 7, label: '7 — Pro III' },
  { tier: 8, label: '8 — Pro II' },
  { tier: 9, label: '9 — Pro I' },
  { tier: 10, label: '10 — Star III' },
  { tier: 11, label: '11 — Star II' },
  { tier: 12, label: '12 — Star I' },
  { tier: 13, label: '13 — Elite III' },
  { tier: 14, label: '14 — Elite II' },
  { tier: 15, label: '15 — Elite I' },
  { tier: 16, label: '16 — Master III' },
  { tier: 17, label: '17 — Master II' },
  { tier: 18, label: '18 — Master I' },
  { tier: 19, label: '19 — Legendary III' },
  { tier: 20, label: '20 — Legendary II' },
  { tier: 21, label: '21 — Legendary I' },
];

// Two-line cell renderer for the Created / Last Login / Last Active columns.
// Date on top, time below in muted text — same-day events are still
// distinguishable without making the column 200px wide.
const renderDateWithTime = (date: string | null | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return (
    <div style={{ lineHeight: 1.2 }}>
      <div>{d.toLocaleDateString()}</div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {d.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    </div>
  );
};

export const PlayerList: React.FC = () => {
  const { show, list } = useNavigation();
  const [searchText, setSearchText] = useState('');
  const [platform, setPlatform] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [accountType, setAccountType] = useState<string | undefined>(undefined);
  const [cohort, setCohort] = useState<CohortFilter>('all');
  const [divisionTier, setDivisionTier] = useState<number | undefined>(undefined);
  const screens = useBreakpoint();

  const { tableProps, setFilters } = useTable<Player>({
    resource: 'players',
    syncWithLocation: true,
    pagination: {
      pageSize: screens.xs ? 10 : 20,
    },
  });

  const applyFilters = () => {
    const filters: CrudFilter[] = [];

    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    if (platform) {
      filters.push({ field: 'platform', operator: 'eq', value: platform });
    }
    if (accountType !== undefined) {
      filters.push({
        field: 'isGuest',
        operator: 'eq',
        value: accountType === 'guest',
      });
    }

    // Status filter maps to different backend params
    switch (status) {
      case 'active':
        filters.push({ field: 'isActive', operator: 'eq', value: true });
        break;
      case 'inactive':
        filters.push({ field: 'isActive', operator: 'eq', value: false });
        filters.push({ field: 'isDeleted', operator: 'eq', value: false });
        break;
      case 'banned':
        filters.push({ field: 'isBanned', operator: 'eq', value: true });
        break;
      case 'deleted':
        filters.push({ field: 'isDeleted', operator: 'eq', value: true });
        break;
    }

    // Cohort filter (§3.4) — backend accepts cohort=no-matches | quickplay-only | at-risk | churned
    if (cohort !== 'all') {
      filters.push({ field: 'cohort', operator: 'eq', value: cohort });
    }

    // §3.4a Division-tier filter — backend accepts divisionTier=1..21
    if (divisionTier !== undefined) {
      filters.push({
        field: 'divisionTier',
        operator: 'eq',
        value: divisionTier,
      });
    }

    setFilters(filters, 'replace');
  };

  const clearFilters = () => {
    setSearchText('');
    setPlatform(undefined);
    setStatus('all');
    setAccountType(undefined);
    setCohort('all');
    setDivisionTier(undefined);
    setFilters([], 'replace');
  };

  // A cohort count in the summary panels shows just that cohort, so the list matches the game-wide count.
  const showCohort = (next: PlayerCohort) => {
    setSearchText('');
    setPlatform(undefined);
    setStatus('all');
    setAccountType(undefined);
    setCohort(next);
    setDivisionTier(undefined);
    setFilters([{ field: 'cohort', operator: 'eq', value: next }], 'replace');
  };

  const hasActiveFilters =
    searchText ||
    platform ||
    status !== 'all' ||
    accountType !== undefined ||
    cohort !== 'all' ||
    divisionTier !== undefined;

  const columns = [
    {
      title: 'Player',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 150,
      sorter: true,
      render: (name: string) => (
        <Space>
          <UserOutlined />
          <Text strong style={{ fontSize: screens.xs ? 12 : 14 }}>
            {name || 'Unknown'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 90,
      render: (p: string) => (
        <Tag color={p === 'android' ? 'green' : 'blue'}>{p?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Logins',
      dataIndex: 'loginCount',
      key: 'loginCount',
      width: 80,
      sorter: true,
      responsive: ['md'] as ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl')[],
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      width: 90,
      render: (isActive: boolean, record: Player) => {
        if (record.displayName === 'Deleted User') {
          return <Tag color="default">Deleted</Tag>;
        }
        if (record.isBanned) {
          return <Tag color="error">Banned</Tag>;
        }
        return (
          <Tag color={isActive ? 'success' : 'warning'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      responsive: ['lg'] as ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl')[],
      render: renderDateWithTime,
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 120,
      sorter: true,
      responsive: ['lg'] as ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl')[],
      render: renderDateWithTime,
    },
    {
      // §2.4b — "Last Active" = denormalised game_users.lastActiveAt,
      // bumped on /match/complete AND on every login/token-refresh. Shows
      // "-" only for users we've literally never seen do anything since
      // the column was backfilled.
      title: (
        <Space size={4}>
          <span>Last Active</span>
          <Tooltip
            title={
              <span>
                Most recent engagement: bumped on <strong>match completion</strong>,{' '}
                <strong>login</strong>, OR <strong>token refresh</strong>. Shows{' '}
                <Text style={{ color: '#fff' }}>&quot;-&quot;</Text> only for users with no recorded
                activity since the column was backfilled (2026-05-27). Use Last Login if you
                specifically want &quot;most recent re-authentication&quot;.
              </span>
            }
          >
            <InfoCircleOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: 140,
      sorter: true,
      responsive: ['sm'] as ('xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl')[],
      render: renderDateWithTime,
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      fixed: 'right' as const,
      render: (_: unknown, record: Player) => (
        <Button type="link" size="small" onClick={() => show('players', record.id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <Title level={screens.xs ? 4 : 3} style={{ marginBottom: 4 }}>
            Players
          </Title>
          <Text type="secondary">Search and manage player accounts</Text>
        </div>
      </div>

      <PlayerSummaryPanels onCohort={showCohort} />

      <Card bodyStyle={{ padding: screens.xs ? 12 : 24 }}>
        {/* Filter bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search by ID or name"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={applyFilters}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Platform"
              value={platform}
              onChange={setPlatform}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="android">Android</Select.Option>
              <Select.Option value="ios">iOS</Select.Option>
              <Select.Option value="unity_editor">Unity Editor</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Status"
              value={status}
              onChange={(val) => setStatus(val as StatusFilter)}
              style={{ width: '100%' }}
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="banned">Banned</Select.Option>
              <Select.Option value="deleted">Deleted</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Account Type"
              value={accountType}
              onChange={setAccountType}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="guest">Guest</Select.Option>
              <Select.Option value="linked">Linked</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Cohort"
              value={cohort}
              onChange={(val) => setCohort(val as CohortFilter)}
              style={{ width: '100%' }}
              title="Retention / engagement cohort (also set by the Player activity and Players with no matches panels above). For long-lived segments (whales, subscribers, geography, etc.) see the Cohorts module."
              // The 4 options here are transient retention drill-downs
              // (SQL filters only). The Cohorts module manages persistent,
              // rule-driven segments (whale/dolphin/geo/etc.) used by
              // offers + products. Footer link below points admins there.
              popupRender={(menu) => (
                <>
                  {menu}
                  <div
                    style={{
                      borderTop: '1px solid #f0f0f0',
                      padding: '6px 12px',
                      cursor: 'pointer',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => list('cohorts')}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Manage rule-driven segments →
                    </Text>
                  </div>
                </>
              )}
            >
              <Select.Option value="all">All cohorts</Select.Option>
              <Select.Option value="no-matches">{COHORT_LABELS['no-matches']}</Select.Option>
              <Select.Option value="quickplay-only">
                {COHORT_LABELS['quickplay-only']}
              </Select.Option>
              <Select.Option value="at-risk">{COHORT_LABELS['at-risk']}</Select.Option>
              <Select.Option value="churned">{COHORT_LABELS['churned']}</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Division"
              value={divisionTier}
              onChange={(val) => setDivisionTier(val)}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              title="Filter by division tier (1=Rookie III … 21=Legendary I). Drill-down from the Legendary leaderboard."
            >
              {DIVISION_OPTIONS.map((opt) => (
                <Select.Option key={opt.tier} value={opt.tier}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Space>
              <Button type="primary" onClick={applyFilters} icon={<FilterOutlined />}>
                {screens.xs ? '' : 'Filter'}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} icon={<ClearOutlined />}>
                  {screens.xs ? '' : 'Clear'}
                </Button>
              )}
              <ExportButton
                options={[
                  // §3.4 + §3.4a — base player export AND a filter-aware
                  // variant that exports exactly the cohort + division
                  // tier the admin has selected on screen. If neither
                  // filter is set this falls through to the base export.
                  {
                    key: 'players-filtered',
                    label:
                      cohort !== 'all' || divisionTier !== undefined
                        ? `Players (filtered${cohort !== 'all' ? ` • cohort=${cohort}` : ''}${
                            divisionTier !== undefined ? ` • tier=${divisionTier}` : ''
                          })`
                        : 'Players (all)',
                    exportFn: (fromDate?: string, toDate?: string) =>
                      adminApi.exportPlayersFiltered({
                        fromDate,
                        toDate,
                        cohort: cohort !== 'all' ? cohort : undefined,
                        divisionTier,
                      }),
                    filename:
                      cohort !== 'all' || divisionTier !== undefined
                        ? `players_${cohort !== 'all' ? `cohort_${cohort}_` : ''}${
                            divisionTier !== undefined ? `tier_${divisionTier}_` : ''
                          }`
                        : 'players',
                  },
                  exportOptions.players,
                  exportOptions.ftueBugCohort,
                  exportOptions.partialSquadCohort,
                  exportOptions.suspectMatches,
                ]}
                buttonText={screens.xs ? '' : 'Export'}
              />
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          scroll={{ x: 600 }}
          size={screens.xs ? 'small' : 'middle'}
          pagination={{
            ...tableProps.pagination,
            size: 'small',
            showSizeChanger: !screens.xs,
            showTotal: screens.sm ? (total) => `Total ${total} players` : undefined,
          }}
        />
      </Card>
    </div>
  );
};
